import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { dbService } from '../../services/firebase';
import { BlogPost } from '../../types';
import { STOCK_IMAGES } from '../../config/constants';
import { EmptySiteNotice } from '../common/EmptySiteNotice';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  CheckCircle2,
  Clock,
  Search,
  Sparkles,
  X,
  Check,
} from 'lucide-react';

export const BlogManager: React.FC<{ onNavigate?: (view: string) => void }> = ({ onNavigate }) => {
  const { activeSite } = useSite();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);

  const [formData, setFormData] = useState<Partial<BlogPost>>({
    title: '',
    slug: '',
    summary: '',
    content: '',
    category: 'Rehber',
    tags: ['Sektör', 'Yenilikler'],
    coverImage: STOCK_IMAGES[2]?.url || '',
    authorName: 'Adiosoft Editör',
    readTimeMinutes: 5,
    status: 'PUBLISHED',
  });

  const loadPosts = async () => {
    if (!activeSite) return;
    setLoading(true);
    const data = await dbService.getBlogPosts(activeSite.id);
    setPosts(data);
    setLoading(false);
  };

  useEffect(() => {
    loadPosts();
  }, [activeSite?.id]);

  if (!activeSite) {
    return (
      <EmptySiteNotice
        title="Blog & İçerik Yönetimi İçin Aktif Web Sitesi Gereklidir"
        description="Makale yazmak, rehberler paylaşmak ve içerik pazarlaması yapmak için lütfen bir web sitesi oluşturun veya mevcut sitelerinizden birini aktif yapın."
        onOpenCreateSite={() => onNavigate && onNavigate('sites')}
        onReturnToDashboard={() => onNavigate && onNavigate('dashboard')}
      />
    );
  }

  const handleOpenCreate = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      slug: '',
      summary: '',
      content: '',
      category: activeSite.industry === 'YACHT_RENTAL' ? 'Mavi Tur Rehberi' : 'Sektörel Rehber',
      tags: ['Rehber', 'Trendler'],
      coverImage: STOCK_IMAGES[2]?.url || '',
      authorName: activeSite.name + ' Ekibi',
      readTimeMinutes: 4,
      status: 'PUBLISHED',
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (post: BlogPost) => {
    setEditingPost(post);
    setFormData(post);
    setIsModalOpen(true);
  };

  const handleDelete = async (postId: string) => {
    if (confirm('Bu makaleyi silmek istediğinizden emin misiniz?')) {
      await dbService.deleteBlogPost(activeSite.id, postId);
      loadPosts();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title) return;

    const slug = formData.slug || (formData.title || '').toLowerCase().replace(/[^a-z0-9]/g, '-');
    const postData: BlogPost = {
      id: editingPost ? editingPost.id : `post_${Date.now().toString(36)}`,
      siteId: activeSite.id,
      title: formData.title || '',
      slug,
      summary: formData.summary || '',
      content: formData.content || '',
      category: formData.category || 'Genel',
      tags: formData.tags || [],
      coverImage: formData.coverImage || STOCK_IMAGES[2].url,
      authorName: formData.authorName || 'Yazar',
      readTimeMinutes: Number(formData.readTimeMinutes) || 4,
      publishedAt: editingPost ? editingPost.publishedAt : Date.now(),
      status: (formData.status as any) || 'PUBLISHED',
      seoTitle: formData.title || '',
      seoDescription: formData.summary || '',
    };

    await dbService.saveBlogPost(activeSite.id, postData);
    setIsModalOpen(false);
    loadPosts();
  };

  const filteredPosts = posts.filter((p) => {
    const q = (searchQuery || '').toLowerCase();
    return (
      (p.title || '').toLowerCase().includes(q) ||
      (p.category ? p.category.toLowerCase().includes(q) : false)
    );
  });

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto space-y-6 animate-in fade-in select-none">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-indigo-600">
            <FileText className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-wider">İçerik Pazarlaması</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight mt-1">
            Blog & Makale Yönetimi
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Google SEO uyumlu sektörel içerikler üreterek web sitenizin arama motorlarındaki görünürlüğünü artırın.
          </p>
        </div>

        <button
          onClick={handleOpenCreate}
          className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs shadow-lg shadow-indigo-500/20 transition flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Yeni Makale Yaz
        </button>
      </div>

      {/* Search & Stats Bar */}
      <div className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between gap-4">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
          <input
            type="text"
            placeholder="Makale başlığı veya kategori ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <span className="text-xs font-semibold text-slate-500">{posts.length} Makale Mevcut</span>
      </div>

      {/* Blog Cards Grid */}
      {loading ? (
        <div className="text-center py-12 text-xs text-slate-400">Yazılar yükleniyor...</div>
      ) : filteredPosts.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-2xl border border-slate-200 space-y-3">
          <FileText className="w-10 h-10 text-slate-300 mx-auto" />
          <h3 className="font-bold text-sm text-slate-700">Henüz Makale Yayınlanmadı</h3>
          <p className="text-xs text-slate-400 max-w-sm mx-auto">
            İlk blog makalenizi oluşturarak ziyaretçilerinize faydalı rehberler sunun.
          </p>
          <button
            onClick={handleOpenCreate}
            className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-500 transition"
          >
            İlk Makaleyi Yaz
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post) => (
            <div
              key={post.id}
              className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition flex flex-col justify-between"
            >
              <div className="relative h-48 overflow-hidden bg-slate-100">
                <img
                  src={post.coverImage}
                  alt={post.title}
                  className="w-full h-full object-cover hover:scale-105 transition duration-500"
                />
                <div className="absolute top-3 left-3 px-2.5 py-1 rounded-lg bg-slate-900/80 backdrop-blur-md text-white text-[10px] font-bold">
                  {post.category}
                </div>
                <div className="absolute top-3 right-3">
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      post.status === 'PUBLISHED'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-amber-500 text-white'
                    }`}
                  >
                    {post.status === 'PUBLISHED' ? 'Yayında' : 'Taslak'}
                  </span>
                </div>
              </div>

              <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <h3 className="font-bold text-base text-slate-900 line-clamp-2">{post.title}</h3>
                  <p className="text-xs text-slate-500 line-clamp-3 mt-1.5">{post.summary}</p>
                </div>

                <div className="flex items-center justify-between text-[11px] text-slate-400 pt-3 border-t border-slate-100">
                  <span>{post.authorName}</span>
                  <span>{post.readTimeMinutes} dk okuma</span>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={() => handleOpenEdit(post)}
                    className="flex-1 py-2 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition flex items-center justify-center gap-1.5"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    Düzenle
                  </button>
                  <button
                    onClick={() => handleDelete(post.id)}
                    className="p-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 transition"
                    title="Sil"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in select-none">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-3xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between text-white">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-base font-bold">
                    {editingPost ? 'Makaleyi Düzenle' : 'Yeni Blog Makalesi Oluştur'}
                  </h3>
                  <p className="text-xs text-slate-400">SEO uyumlu başlık ve içerik girin.</p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-4 text-slate-200 text-xs">
              <div>
                <label className="block font-semibold mb-1 text-slate-300">Makale Başlığı *</label>
                <input
                  required
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Örn: 2026 Mavi Yolculuk ve Yat Kiralama Rehberi"
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-semibold mb-1 text-slate-300">Kategori</label>
                  <input
                    type="text"
                    value={formData.category || ''}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="Rehber, İpuçları, Haberler..."
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-1 text-slate-300">Yazar Adı</label>
                  <input
                    type="text"
                    value={formData.authorName || ''}
                    onChange={(e) => setFormData({ ...formData, authorName: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold mb-1 text-slate-300">Kapak Fotoğrafı URL</label>
                <input
                  type="text"
                  value={formData.coverImage || ''}
                  onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                />
                <div className="flex gap-2 overflow-x-auto py-2">
                  {STOCK_IMAGES.slice(0, 5).map((img, i) => (
                    <img
                      key={i}
                      src={img.url}
                      alt={img.name}
                      onClick={() => setFormData({ ...formData, coverImage: img.url })}
                      className="w-16 h-12 rounded-lg object-cover cursor-pointer border border-slate-700 hover:border-indigo-500 shrink-0"
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="block font-semibold mb-1 text-slate-300">Kısa Özet (Meta Description)</label>
                <textarea
                  rows={2}
                  value={formData.summary || ''}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  placeholder="Makalenin 2-3 cümlelik özeti..."
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              <div>
                <label className="block font-semibold mb-1 text-slate-300">Makale İçeriği (Markdown / Metin)</label>
                <textarea
                  rows={6}
                  value={formData.content || ''}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Makalenin tüm içeriğini buraya yazın..."
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-indigo-500 outline-none font-mono"
                />
              </div>

              <div className="pt-3 border-t border-slate-800 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold shadow-lg shadow-indigo-500/20 flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Makaleyi Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
